import {
  Tbody,
  Tr,
  Td,
  Box,
  Table as ChakraTable,
  VStack,
  HStack,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
  Avatar,
} from '@chakra-ui/react';
import { AutoResizeTextarea, Button, Checkbox, IconButton, Tooltip } from 'Atoms';
import { Typography } from 'Tokens';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { ArrowNarrowRightIcon, CornerDownRightIcon } from 'Tokens/Icons/Direction';
import { CommentIcon } from 'Tokens/Icons/Function';
import {
  MDRM_QUESTIONS,
  MdrmAnswers,
  MdrmAnswerType,
  MdrmType,
  MdrmTypeEnum,
  OnMdrmAnswerChangeType,
} from './MDRM.types';
import { MetricsTableData } from '../MetricAnswers.hooks';
import { useSearchParams } from 'react-router-dom';
import { useNotifications } from 'Features';
import { DescriptionIcon } from 'Tokens/Icons/Data';
import { AggregatedQualitativeAnswers } from '../AggregatedMetrics';
import { useDebounceCallback } from 'usehooks-ts';
import { SelectedRowData } from '../MetricsTable/MetricsTableComponents/SelectedRows/SelectedRows';
import { FrequencyEnums, QUARTERS_ARRAY, TimePeriods, TimePeriodsEnums } from '../../Requirement';
import { uniq } from 'lodash';
import { EmptyOwner } from 'Atoms/EmptyOwner';
import { getUsernameColor } from 'Organisms/UserAvatar/UserAvatar.utils';
import { GetDatapointMdrmAnswersDocument_, useUpsertDatapointMdrmMutation } from 'models';

const MdrmInput = ({
  mdrmId,
  onMdrmAnswerChange,
  answer,
}: {
  mdrmId: MdrmType;
  answer?: MdrmAnswerType;
  onMdrmAnswerChange: OnMdrmAnswerChangeType;
}) => {
  const [localValue, setLocalValue] = useState<string>(answer?.value ?? '');
  const [hasStartedTyping, setHasStartedTyping] = useState(false);

  useEffect(() => {
    if (!hasStartedTyping) setLocalValue(answer?.value ?? '');
  }, [answer]);

  const debounceSave = useDebounceCallback((newValue) => {
    onMdrmAnswerChange(newValue ?? '', mdrmId, answer?.ownerId);
  }, 500);

  const handleChange = (val: string) => {
    setHasStartedTyping(true);
    setLocalValue(val);
    debounceSave(val);
  };

  return (
    <AutoResizeTextarea
      variant="ghost"
      minH="56px"
      onChange={({ target }) => handleChange(target.value)}
      placeholder="Write your answer"
      value={localValue ?? ''}
      borderRadius="6px"
      lineHeight="20px"
      size="md"
      isDisabled={answer?.hasOptedOut}
    />
  );
};

const CommentsCounter = ({
  mdrm,
  onClick,
}: {
  mdrm: MdrmAnswerType | undefined;
  onClick: () => void;
}) => {
  const { notifications } = useNotifications();

  const commentsCount = useMemo(
    () => mdrm?.thread?.comments_aggregate.aggregate?.count ?? 0,
    [mdrm]
  );

  const hasUnread = useMemo(
    () =>
      notifications.some(
        (notf) => notf.comment.thread.esrsDatapointMdrm?.id === mdrm?.id && !notf.isRead
      ),
    [notifications, mdrm]
  );

  return (
    <Button
      size="sm"
      variant="ghost"
      leftIcon={<CommentIcon color={hasUnread ? 'text.action' : 'text.muted'} />}
      onClick={onClick}
    >
      <Typography
        variant="bodyStrong"
        color={hasUnread ? 'text.action' : 'text.muted'}
        opacity={commentsCount === 0 ? 0.2 : 1}
      >
        {commentsCount}
      </Typography>
    </Button>
  );
};

export const MDRMTable = ({
  metric,
  answers,
  datapointId,
  onMdrmAnswerChange,
  setOpenMdrm,
  setOpenComments,
  setRowData,
  selectedRows,
  selectedQuarter,
  setSelectedRows,
  isTableHovered,
}: {
  metric: MetricsTableData;
  datapointId?: string;
  answers: MdrmAnswers;
  onMdrmAnswerChange: OnMdrmAnswerChangeType;
  setOpenMdrm: (open: MdrmAnswerType | undefined) => void;
  setOpenComments: (open: boolean) => void;
  selectedQuarter: TimePeriods;
  selectedRows: SelectedRowData[];
  setSelectedRows: Dispatch<SetStateAction<SelectedRowData[]>>;
  setRowData: (
    param: (MetricsTableData & { sourceData?: AggregatedQualitativeAnswers[number] }) | undefined
  ) => void;
  isTableHovered: boolean;
}) => {
  const isEntitySpecific = useMemo(
    () =>
      metric.metric.additionalTypes.some((type) => {
        return type.additionalType.reference === 'Entity-specific';
      }),
    [metric.metric.additionalTypes]
  );
  const [upsertMdrmDatapoint] = useUpsertDatapointMdrmMutation();
  const [hoveredRef, setHoveredRef] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();

  const openMdrmId = useMemo(() => {
    return searchParams.get('openMdrm');
  }, [searchParams]);

  const handleRowClick = (
    row: { id: string; question: string; answer: MdrmAnswerType | undefined },
    isComments: boolean
  ) => {
    if (!row.answer) {
      upsertMdrmDatapoint({
        variables: {
          object: {
            datapointId,
            mdrmRef: row.id,
          },
        },
        refetchQueries: [GetDatapointMdrmAnswersDocument_],
      }).then(() => {
        setOpenMdrm(row.answer);
        setRowData(undefined);
        setOpenComments(isComments);
        // Add a param to the url to tell that the mdrm sidebar is open
        setSearchParams({ openMdrm: row?.id ?? '' });
      });
    } else {
      setOpenMdrm(row.answer);
      setRowData(undefined);
      setOpenComments(isComments);
      // Add a param to the url to tell that the mdrm sidebar is open
      setSearchParams({ openMdrm: row?.id ?? '' });
    }
  };

  const mappedMdrms = useMemo(() => {
    return MDRM_QUESTIONS.filter((q) => {
      if (q.id === MdrmTypeEnum.entitySpecific) {
        return isEntitySpecific;
      }
      return true;
    }).map((q) => {
      return {
        id: q.id,
        question: q.question,
        answer: answers[q.id],
      };
    });
  }, [answers, isEntitySpecific]);

  // Check if there is an openMdrm in the url, then set the openMdrm state to thr corresponding one
  useEffect(() => {
    if (openMdrmId) {
      const mdrm = mappedMdrms.find((mdrm) => mdrm.answer?.id === openMdrmId);
      if (mdrm) {
        setOpenMdrm(mdrm.answer);
      }
    }
  }, [openMdrmId, mappedMdrms]);

  const timeframe =
    metric.metric.materialMetrics?.[0]?.frequency === FrequencyEnums.yearly
      ? TimePeriodsEnums.year
      : QUARTERS_ARRAY.find((quarter) => quarter === selectedQuarter);

  const getDueDate = (dueDateStr?: string) => {
    if (!dueDateStr) return null;

    const date = new Date(dueDateStr);

    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <VStack w="100%" spacing="10px" alignItems="start">
      <HStack spacing="6px">
        <CornerDownRightIcon />
        <Typography variant="h4">MDR-M for {metric.metric.title}</Typography>
      </HStack>
      <ChakraTable
        borderTop="1px solid"
        borderColor="border.decorative"
        borderRadius="8px"
        w="100%"
      >
        <Tbody>
          {mappedMdrms.map((row, index, arr) => {
            const key = {
              rowKey: `${metric.metric.reference}${metric.tagCombination}${timeframe}${row.id}`,
              tagCombination: metric.tagCombination,
              metricRef: metric.metric.reference,
              timeframe: timeframe ?? '',
              tags: metric.tags,
              mdrmRef: row.id,
            };

            const answer = row.answer;
            const hideImage =
              answer?.task?.owner?.avatarUrl?.includes('default=blank') ||
              answer?.task?.owner?.avatarUrl?.includes('d=blank');
            const hasOptedOut = answer?.hasOptedOut;
            return (
              <Tr
                key={row.id}
                borderColor="border.decorative"
                height="48px"
                alignItems="start"
                bg={answer?.id === openMdrmId ? 'bg.hover' : ''}
                _hover={{
                  transition: '0.15s ease-in-out',
                  bg: 'bg.hover',
                }}
              >
                <Td
                  w="32px"
                  padding="14px 8px"
                  verticalAlign="top"
                  borderColor="border.decorative"
                  borderBottom={index === arr.length - 1 ? 'none' : undefined}
                >
                  {!!(isTableHovered || selectedRows.length) && (
                    <Checkbox
                      isChecked={selectedRows?.some(
                        (selectedRow) => key.rowKey === selectedRow.rowKey
                      )}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                          setSelectedRows(uniq([...selectedRows, key]));
                        } else {
                          setSelectedRows(
                            selectedRows.filter((selectedRow) => key.rowKey !== selectedRow.rowKey)
                          );
                        }
                      }}
                    />
                  )}
                </Td>
                <Td
                  w="50%"
                  padding="14px 8px"
                  verticalAlign="top"
                  borderColor="border.decorative"
                  borderBottom={index === arr.length - 1 ? 'none' : undefined}
                >
                  <HStack
                    w="100%"
                    alignItems="start"
                    className="metricTitle"
                    textDecorationColor={answer?.id === openMdrmId ? 'text.hint' : 'transparent'}
                    textUnderlineOffset={answer?.id === openMdrmId ? '2px' : '4px'}
                    transition="0.15s"
                    spacing="8px"
                    cursor="pointer"
                    onClick={() => handleRowClick(row, false)}
                    _hover={{
                      textDecoration: 'underline',
                      textDecorationColor: 'text.hint',
                      textUnderlineOffset: '2px',
                    }}
                  >
                    <Tooltip label="MDR-M" placement="left">
                      <Box mt="2px">
                        <DescriptionIcon color={hasOptedOut ? 'text.hint' : ''} />
                      </Box>
                    </Tooltip>
                    <Typography variant="body" color={hasOptedOut ? 'text.hint' : ''}>
                      {row.question}
                    </Typography>
                  </HStack>
                </Td>
                <Td
                  padding="6px 8px"
                  verticalAlign="top"
                  borderColor="border.decorative"
                  borderBottom={index === arr.length - 1 ? 'none' : undefined}
                >
                  <Box w="100%">
                    <MdrmInput
                      mdrmId={row.id}
                      answer={answer}
                      onMdrmAnswerChange={onMdrmAnswerChange}
                    />
                  </Box>
                </Td>
                <Td
                  w="54px"
                  padding="14px 8px"
                  verticalAlign="top"
                  borderColor="border.decorative"
                  borderBottom={index === arr.length - 1 ? 'none' : undefined}
                >
                  {row.answer?.task?.owner ? (
                    <Popover isOpen={hoveredRef === row.answer.mdrmRef}>
                      <PopoverAnchor>
                        <Avatar
                          onMouseEnter={() => setHoveredRef(row.answer?.mdrmRef ?? '')}
                          onMouseLeave={() => setHoveredRef('')}
                          name={row.answer.task.owner?.displayName}
                          src={hideImage ? '' : row.answer.task.owner?.avatarUrl}
                          bgColor={getUsernameColor(row.answer.task.owner?.displayName)}
                          size="xs"
                        />
                      </PopoverAnchor>
                      <PopoverContent
                        bg="bg.accent"
                        border="none"
                        w="fit-content"
                        boxShadow="none !important"
                      >
                        <PopoverArrow bg="bg.accent" border="none" />
                        <VStack alignItems="start" gap="2px" padding="8px">
                          <Typography variant="bodyStrong" color="text.onAccent">
                            {row.answer.task.owner.displayName}
                          </Typography>

                          <Typography color="text.onAccent" variant="micro">
                            {!!getDueDate(row.answer.task.dueDate)
                              ? `Due ${getDueDate(row.answer.task.dueDate)}`
                              : 'No deadline'}
                          </Typography>
                        </VStack>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <EmptyOwner size="20px" />
                  )}
                </Td>
                <Td
                  w="91px"
                  padding="10px 6px"
                  verticalAlign="top"
                  borderColor="border.decorative"
                  borderBottom={index === arr.length - 1 ? 'none' : undefined}
                >
                  <HStack gap="2px">
                    <CommentsCounter mdrm={answer} onClick={() => handleRowClick(row, true)} />
                    <IconButton
                      className="metricArrow"
                      aria-label="side-bar"
                      size="sm"
                      variant="ghost"
                      icon={<ArrowNarrowRightIcon />}
                      onClick={() => handleRowClick(row, false)}
                    />
                  </HStack>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </ChakraTable>
    </VStack>
  );
};
